// HEADER-DOCS-026 Part 2: Files tab inside the chat panel.
// Lists every file shared in one student<->counselor thread, grouped by date,
// newest first. Data comes from the chat-origin documents that HEADER-DOCS-006
// aggregates (GET api/cam-mirror/documents?conversationId=...), so deleting a chat
// attachment also removes it here. Student + counselor only: the caller must not
// render this for parents (the backend also returns 403 for parents).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';

import { documentsApi, DocumentDto, httpStatusOf } from 'services/camMirrorService';
import { triggerDownload } from 'utils/triggerDownload';

const PAGE_SIZE = 20;

export interface ChatFilesTabProps {
  studentId: number;
  conversationId: string;
  currentUserId: number;
  /** Display name of the other person on the thread, used as the sender label. */
  otherPartyName: string;
}

/** Human-readable size, e.g. "1.2 MB". */
export function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${i === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

/** Short type label from the MIME type or the file extension, e.g. "PDF". */
export function fileTypeLabel(mimeType: string | undefined, fileName: string): string {
  const ext = fileName.includes('.') ? fileName.split('.').pop() || '' : '';
  if (ext && ext.length <= 5) return ext.toUpperCase();
  const sub = (mimeType || '').split('/')[1] || '';
  return sub ? sub.toUpperCase().slice(0, 6) : 'FILE';
}

/** Groups documents by local calendar day, keeping the incoming (newest-first) order. */
export function groupByDay(docs: DocumentDto[]): Array<{ day: string; items: DocumentDto[] }> {
  const groups: Array<{ day: string; items: DocumentDto[] }> = [];
  for (const doc of docs) {
    const day = new Date(doc.createdAt).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const last = groups[groups.length - 1];
    if (last && last.day === day) last.items.push(doc);
    else groups.push({ day, items: [doc] });
  }
  return groups;
}

export const ChatFilesTab: React.FC<ChatFilesTabProps> = ({
  studentId,
  conversationId,
  currentUserId,
  otherPartyName,
}) => {
  const [items, setItems] = useState<DocumentDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Ignore responses that arrive after the thread changed or the tab unmounted.
  const requestSeq = useRef(0);

  const loadFirstPage = useCallback(async () => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(null);
    try {
      const page = await documentsApi.list({ studentId, conversationId, limit: PAGE_SIZE });
      if (seq !== requestSeq.current) return;
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(httpStatusOf(err) === 403 ? 'You do not have access to these files.' : 'Could not load files.');
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  }, [studentId, conversationId]);

  useEffect(() => {
    loadFirstPage();
    return () => {
      requestSeq.current += 1;
    };
  }, [loadFirstPage]);

  const loadMore = async () => {
    if (!nextCursor || isLoadingMore) return;
    const seq = requestSeq.current;
    setIsLoadingMore(true);
    try {
      const page = await documentsApi.list({ studentId, conversationId, limit: PAGE_SIZE, cursor: nextCursor });
      if (seq !== requestSeq.current) return;
      setItems((prev) => [...prev, ...page.items]);
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch {
      if (seq === requestSeq.current) setNotice('Could not load more files.');
    } finally {
      if (seq === requestSeq.current) setIsLoadingMore(false);
    }
  };

  const download = async (doc: DocumentDto) => {
    try {
      // Always ask for a fresh presigned URL; the one in the list response expires after 5 minutes.
      const url = (await documentsApi.getDownloadUrl(doc.externalId)).downloadUrl;
      // Download in place (the URL is signed as an attachment) so no empty tab is left behind.
      triggerDownload(url, doc.displayName);
    } catch (err) {
      if (httpStatusOf(err) === 410) {
        // File is gone from storage; the backend soft-deleted the row, so drop it here too.
        setItems((prev) => prev.filter((d) => d.externalId !== doc.externalId));
        setNotice('This file is no longer available.');
      } else {
        setNotice('Could not download this file.');
      }
    }
  };

  const senderLabel = (doc: DocumentDto) =>
    Number(doc.createdBy?.id) === Number(currentUserId) ? 'You' : otherPartyName || 'Them';

  const timeLabel = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

  if (isLoading) {
    return (
      <div className="chat-files-tab chat-files-center" aria-busy="true">
        <CircularProgress size={20} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="chat-files-tab chat-files-center" role="alert">
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
        <Button size="small" onClick={loadFirstPage}>
          Retry
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="chat-files-tab chat-files-center">
        <Typography variant="body2" color="text.secondary">
          No files shared yet
        </Typography>
      </div>
    );
  }

  return (
    <div className="chat-files-tab">
      {notice && (
        <div className="chat-files-notice" role="status">
          <span>{notice}</span>
          <button type="button" className="chat-files-notice-close" onClick={() => setNotice(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}
      {groupByDay(items).map((group) => (
        <section key={group.day} className="chat-files-group" aria-label={group.day}>
          <div className="chat-files-day">{group.day}</div>
          <ul className="chat-files-list">
            {group.items.map((doc) => (
              <li key={doc.externalId} className="chat-files-row">
                <span className="chat-files-type" aria-hidden="true">
                  {fileTypeLabel(doc.attachment?.mimeType, doc.displayName)}
                </span>
                <div className="chat-files-info">
                  <span className="chat-files-name" title={doc.displayName}>
                    {doc.displayName}
                  </span>
                  <span className="chat-files-meta">
                    {senderLabel(doc)} · {timeLabel(doc.createdAt)}
                    {doc.attachment ? ` · ${formatBytes(doc.attachment.sizeBytes)}` : ''}
                  </span>
                </div>
                <IconButton
                  size="small"
                  className="chat-files-download"
                  onClick={() => download(doc)}
                  disabled={!doc.attachment}
                  aria-label={`Download ${doc.displayName}`}
                  title="Download"
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {hasMore && (
        <div className="chat-files-more">
          <Button size="small" onClick={loadMore} disabled={isLoadingMore}>
            {isLoadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChatFilesTab;
